// src/components/Navbar.js
import React, { useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Typography,
  CircularProgress,
} from "@mui/material";
import {
  Logout,
  Menu as MenuIcon,
  NotificationsNoneOutlined,
  Person,
  SettingsOutlined,
  CheckCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getData, postData } from "../services/request"; // ✅ API functions import karein
import logo from "../assets/logo/Dajoo.png";

const Navbar = ({ onMenuClick, onNavigate, onLogout, user = {} }) => {
  const navigate = useNavigate();
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [profileAnchor, setProfileAnchor] = useState(null);

  // ✅ Notifications states (API se aayenge)
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [markingId, setMarkingId] = useState(null); // Konsi notification mark ho rahi hai

  useEffect(() => {
    const storedUserData = localStorage.getItem("userData");
    const accessToken = localStorage.getItem("accessToken");

    if (!storedUserData || !accessToken) {
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // ✅ API se Unread Notifications fetch karein
  useEffect(() => {
    const fetchNotifications = async () => {
      setLoadingNotifications(true);
      try {
        const response = await getData("/api/auth/notifications/unread");
        if (response?.status === true) {
          setNotifications(response?.data || []);
        } else {
          setNotifications([]);
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
        setNotifications([]);
      } finally {
        setLoadingNotifications(false);
      }
    };

    fetchNotifications();
  }, []);

  const storedUserData = localStorage.getItem("userData");
  const localUser = storedUserData ? JSON.parse(storedUserData) : {};
  const currentUser = Object.keys(localUser).length ? localUser : user;
  const displayName = currentUser?.fullName || "User";
  const role =
    currentUser?.userType === "groupAdmin"
      ? "Group Admin"
      : currentUser?.userType === "groupUser"
        ? "Member"
        : currentUser?.userType || "Member";

  // ✅ Unread count (API se aaye notifications se)
  const unreadCount = notifications.length;

  const initials =
    currentUser?.initials ||
    displayName
      .split(" ")
      .filter(Boolean)
      .map((part) => part[0])
      .join("")
      .slice(0, 2) ||
    "U";

  const closeProfileMenu = () => setProfileAnchor(null);
  const navigateFromMenu = (path) => {
    closeProfileMenu();
    onNavigate(path);
  };

  // ✅ Mark Single Notification as Read
  const handleMarkAsRead = async (notificationId) => {
    setMarkingId(notificationId);
    try {
      const response = await postData("/api/auth/notifications/read-one", {
        id: notificationId,
      });

      if (response?.status === true) {
        // ✅ UI se remove karein
        setNotifications((prev) =>
          prev.filter((item) => item.id !== notificationId),
        );
      } else {
        alert(response?.message || "Failed to mark as read");
      }
    } catch (error) {
      console.error(error);
      alert("Network error. Please try again.");
    } finally {
      setMarkingId(null);
    }
  };

  return (
    <Box
      component="header"
      sx={{
        height: { xs: 72, md: 80 },
        backgroundColor: "#fff",
        borderBottom: "1px solid #e9ebf0",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        px: { xs: 2, md: 4 },
        position: "sticky",
        top: 0,
        zIndex: 20,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <IconButton
          aria-label="Open navigation"
          onClick={onMenuClick}
          sx={{ display: { xs: "inline-flex", md: "none" }, color: "#52057b" }}
        >
          <MenuIcon />
        </IconButton>
        <img src={logo} style={{ width: "100px", borderRadius: "5px" }}></img>
      </Box>

      <Box
        sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 2 } }}
      >
        <IconButton
          aria-label="Notifications"
          onClick={(event) => setNotificationAnchor(event.currentTarget)}
          sx={{ color: "#505a6c" }}
        >
          <Badge
            color="error"
            badgeContent={unreadCount > 0 ? unreadCount : 0}
            overlap="circular"
            sx={{
              "& .MuiBadge-badge": {
                minWidth: 18,
                height: 18,
                fontSize: 10,
              },
            }}
          >
            <NotificationsNoneOutlined />
          </Badge>
        </IconButton>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ my: 1.5, display: { xs: "none", sm: "block" } }}
        />
        <Button
          aria-label={`Open account menu for ${displayName}`}
          aria-controls={profileAnchor ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={Boolean(profileAnchor) ? "true" : undefined}
          onClick={(event) => setProfileAnchor(event.currentTarget)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            p: 0.5,
            color: "#1d2433",
            textTransform: "none",
            borderRadius: 1.5,
            "&:hover": { backgroundColor: "#f6f7fb" },
          }}
        >
          <Avatar
            sx={{
              width: 38,
              height: 38,
              backgroundColor: "#eadcf1",
              color: "#52057b",
              fontWeight: 800,
            }}
          >
            {initials}
          </Avatar>
          <Box
            sx={{
              display: { xs: "none", sm: "block" },
              minWidth: 92,
              textAlign: "left",
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 750 }}>
              {displayName}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#7a8394" }}>
              {role}
            </Typography>
          </Box>
        </Button>
      </Box>

      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={() => setNotificationAnchor(null)}
        MenuListProps={{ "aria-label": "Notifications" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 320,
            borderRadius: 2,
            boxShadow: "0 12px 35px rgba(28, 32, 50, 0.14)",
          },
        }}
      >
        {loadingNotifications ? (
          <Box sx={{ p: 2, textAlign: "center" }}>
            <CircularProgress size={24} sx={{ color: "#52057b" }} />
          </Box>
        ) : notifications.length === 0 ? (
          <MenuItem sx={{ whiteSpace: "normal", py: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                No unread notifications
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#7a8394", mt: 0.3 }}>
                You are all caught up.
              </Typography>
            </Box>
          </MenuItem>
        ) : (
          notifications.map((item, index) => {
            return (
              <MenuItem
                key={item?.id || index}
                sx={{
                  whiteSpace: "normal",
                  py: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#1d2433",
                    }}
                  >
                    {item?.notification || item?.message || "New notification"}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#7a8394", mt: 0.3 }}>
                    {item?.createdAt
                      ? new Date(item.createdAt).toLocaleString()
                      : "Just now"}
                  </Typography>
                </Box>
                {/* ✅ Small "Mark as Read" Button */}
                <Button
                  size="small"
                  startIcon={
                    markingId === item.id ? (
                      <CircularProgress size={14} />
                    ) : (
                      <CheckCircle />
                    )
                  }
                  onClick={(e) => {
                    e.stopPropagation(); // Menu band hone se rokne ke liye
                    handleMarkAsRead(item.id);
                  }}
                  disabled={markingId === item.id}
                  sx={{
                    ml: 1,
                    color: "#52057b",
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: 12,
                    "&:hover": { backgroundColor: "#f7f1fa" },
                  }}
                >
                  Read
                </Button>
              </MenuItem>
            );
          })
        )}
      </Menu>

      <Menu
        id="account-menu"
        anchorEl={profileAnchor}
        open={Boolean(profileAnchor)}
        onClose={closeProfileMenu}
        MenuListProps={{ "aria-labelledby": "account-menu-button" }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 210,
            borderRadius: 2,
            boxShadow: "0 12px 35px rgba(28, 32, 50, 0.14)",
          },
        }}
      >
        <MenuItem onClick={() => navigateFromMenu("/profile")}>
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          Profile
        </MenuItem>
        <MenuItem onClick={() => navigateFromMenu("/settings")}>
          <ListItemIcon>
            <SettingsOutlined fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            closeProfileMenu();
            localStorage.removeItem("accessToken");
            localStorage.removeItem("userData");
            localStorage.removeItem("deviceId");
            localStorage.removeItem("notifications");
            localStorage.removeItem("nin");

            if (onLogout) {
              onLogout();
              return;
            }

            navigate("/", { replace: true });
          }}
          sx={{ color: "#c0392b" }}
        >
          <ListItemIcon>
            <Logout fontSize="small" sx={{ color: "#c0392b" }} />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Navbar;
